import type { FidelityReport } from '../../core/types';

/**
 * 纯文本编解码工具：编码探测、换行归一、Markdown 降级为纯文本。
 * 全部为纯函数，便于单测；不依赖 DOM 与 Node 专有 API。
 */

export interface DecodedText {
  text: string;
  encoding: string;
}

function hasPrefix(bytes: Uint8Array, signature: readonly number[]): boolean {
  if (bytes.length < signature.length) return false;
  return signature.every((value, index) => bytes[index] === value);
}

function decodeWith(bytes: Uint8Array, encoding: string): string | null {
  try {
    return new TextDecoder(encoding, { fatal: false }).decode(bytes);
  } catch {
    return null;
  }
}

function decodeWithStrict(bytes: Uint8Array, encoding: string): string | null {
  try {
    return new TextDecoder(encoding, { fatal: true }).decode(bytes);
  } catch {
    return null;
  }
}

/**
 * 文本编码探测：
 * 1. BOM（UTF-8 / UTF-16LE / UTF-16BE）优先；
 * 2. UTF-8 严格解码通过即视为 UTF-8；
 * 3. 否则尝试 GBK（中文文档常见），最后退回替换式 UTF-8。
 */
export function decodeTextFile(bytes: Uint8Array): DecodedText {
  if (hasPrefix(bytes, [0xef, 0xbb, 0xbf])) {
    return { text: new TextDecoder('utf-8').decode(bytes.subarray(3)), encoding: 'utf-8-bom' };
  }
  if (hasPrefix(bytes, [0xff, 0xfe])) {
    return { text: new TextDecoder('utf-16le').decode(bytes.subarray(2)), encoding: 'utf-16le' };
  }
  if (hasPrefix(bytes, [0xfe, 0xff])) {
    return { text: new TextDecoder('utf-16be').decode(bytes.subarray(2)), encoding: 'utf-16be' };
  }

  const strictUtf8 = decodeWithStrict(bytes, 'utf-8');
  if (strictUtf8 !== null) return { text: strictUtf8, encoding: 'utf-8' };

  const gbk = decodeWith(bytes, 'gbk') ?? decodeWith(bytes, 'gb18030');
  if (gbk !== null && !gbk.includes('\uFFFD')) return { text: gbk, encoding: 'gbk' };

  return { text: new TextDecoder('utf-8').decode(bytes), encoding: 'utf-8-lossy' };
}

export function normalizeNewlines(text: string): string {
  return text.replace(/\r\n?/g, '\n');
}

/** Markdown → 纯文本：保留可见内容，剥离语法标记（层级与语义必然丢失）。 */
export function markdownToPlainText(markdown: string): string {
  const lines = normalizeNewlines(markdown).split('\n');
  const out: string[] = [];
  let inFence = false;

  for (const raw of lines) {
    if (/^\s*```/.test(raw)) {
      inFence = !inFence;
      continue;
    }

    let line = raw;
    if (!inFence) {
      line = line
        .replace(/^\s{0,3}#{1,6}\s+/, '')
        .replace(/^\s{0,3}>\s?/, '')
        .replace(/^\s{0,3}([-*+])\s+/, '')
        .replace(/^\s{0,3}\d+[.)]\s+/, '')
        .replace(/^\s{0,3}(?:[-*_]\s*){3,}$/, '');
    }

    line = line
      .replace(/!\[([^\]]*)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g, '$1 ($2)')
      .replace(/\[([^\]]+)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g, '$1 ($2)')
      .replace(/`{1,3}([^`]*)`{1,3}/g, '$1')
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/__([^_]+)__/g, '$1')
      .replace(/(^|[^*])\*([^*\n]+)\*/g, '$1$2')
      .replace(/(^|[^_\w])_([^_\n]+)_/g, '$1$2');

    out.push(line);
  }

  return `${out.join('\n').replace(/\n{3,}/g, '\n\n').trimEnd()}\n`;
}

export function countNonEmptyLines(text: string): number {
  return text.split('\n').filter((line) => line.trim().length > 0).length;
}

/** 把「编码/换行被规范化」这一事实补进保真度报告，避免用户以为字节完全一致。 */
export function withTextNormalizationNotes(report: FidelityReport, notes: string[]): FidelityReport {
  if (notes.length === 0) return report;
  const changed = [...report.changed];
  for (const note of notes) if (!changed.includes(note)) changed.push(note);
  return { ...report, changed };
}
